import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, X, Upload, Image } from "lucide-react";

interface CommunicationPanelProps {
  issueId: string;
  issue?: {
    phoneNumber?: string;
    name?: string;
  };
}

const CommunicationPanel = ({ issueId, issue }: CommunicationPanelProps) => {
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [emailSubject, setEmailSubject] = useState(
    `Update on Issue #${issueId}`
  );
  const [emailMessage, setEmailMessage] = useState(
    `Hello,\n\nThis is an update regarding the issue you reported (ID: ${issueId}).\n\nRegards,\nRaastaFix Admin Team`
  );
  const [newEmailRecipient, setNewEmailRecipient] = useState("");

  const [smsRecipients, setSmsRecipients] = useState<string[]>(
    issue?.phoneNumber ? [`${issue.name || 'User'} - ${issue.phoneNumber}`] : []
  );
  const [smsMessage, setSmsMessage] = useState(
    `Dear ${issue?.name || 'User'},

Thank you for reporting the issue. We have worked on this and this is the update over the same.

Thanks,
Team Raastafix`
  );
  const [newSmsRecipient, setNewSmsRecipient] = useState("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isWhatsAppSending, setIsWhatsAppSending] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);

  const handleAddEmailRecipient = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter" && newEmailRecipient.trim() !== "") {
      e.preventDefault();
      setEmailRecipients([...emailRecipients, newEmailRecipient.trim()]);
      setNewEmailRecipient("");
    }
  };

  const handleRemoveEmailRecipient = (index: number) => {
    setEmailRecipients(emailRecipients.filter((_, i) => i !== index));
  };

  const handleAddSmsRecipient = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newSmsRecipient.trim() !== "") {
      e.preventDefault();
      setSmsRecipients([...smsRecipients, newSmsRecipient.trim()]);
      setNewSmsRecipient("");
    }
  };

  const handleRemoveSmsRecipient = (index: number) => {
    setSmsRecipients(smsRecipients.filter((_, i) => i !== index));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error("Image size should be less than 5MB");
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }

      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleSendEmail = async () => {
    if (emailRecipients.length === 0) {
      toast.error("Please add at least one recipient");
      return;
    }

    setIsEmailSending(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const emailData: any = {
        to: emailRecipients,
        subject: emailSubject,
        message: emailMessage,
      };

      const response = await fetch(
        `${import.meta.env.VITE_BE_URL}/api/v1/notifications/send-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(emailData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send email");
      }

      toast.success("Email sent successfully", {
        description: `Sent to ${emailRecipients.join(", ")}`,
      });
    } catch (error) {
      console.error("Email sending error:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to send email"
      );
    } finally {
      setIsEmailSending(false);
    }
  };

  const handleSendSms = async () => {
    if (smsRecipients.length === 0) {
      toast.error("Please add at least one recipient");
      return;
    }

    if (!selectedImage) {
      toast.error("Please upload an image before sending WhatsApp message");
      return;
    }

    setIsWhatsAppSending(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      // Extract phone number from recipient (format: "Name - PhoneNumber")
      const recipient = smsRecipients[0];
      const phoneMatch = recipient.match(/- (.+)$/);
      const phoneNumber = phoneMatch ? phoneMatch[1] : recipient;

      // Extract name from recipient
      const nameMatch = recipient.match(/^(.+) -/);
      const name = nameMatch ? nameMatch[1] : 'User';

      const formData = new FormData();
      formData.append('name', name);
      formData.append('phone', phoneNumber);
      formData.append('issueId', issueId);
      formData.append('message', smsMessage);
      
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

              const response = await fetch(
          `${import.meta.env.VITE_BE_URL}/api/v1/feedback/sendFeedbackToUser`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send WhatsApp message");
      }

      toast.success("WhatsApp sent successfully", {
        description: `Sent to ${name} (${phoneNumber})`,
      });

      // Clear the image after successful send
      setSelectedImage(null);
      setImagePreview(null);

    } catch (error) {
      console.error("WhatsApp sending error:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to send WhatsApp message"
      );
    } finally {
      setIsWhatsAppSending(false);
    }
  };

  return (
    <Card className="border-l-2 border-t-2 border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center">
          <Send className="mr-2 h-5 w-5 text-primary" />
          Send Updates & Notifications
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="sms">
          <TabsList className="grid grid-cols-2 mb-4">
            <TabsTrigger value="sms">WhatsApp</TabsTrigger>
            <TabsTrigger value="email">Email</TabsTrigger>
          </TabsList>

          {/* Email Tab */}
          <TabsContent value="email" className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">To:</label>
              <div className="flex flex-wrap gap-2 p-2 border rounded-md mb-2">
                {emailRecipients.map((recipient, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="flex items-center gap-1 py-1"
                  >
                    <span>{recipient}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveEmailRecipient(index)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <Input
                  type="email"
                  placeholder="Type email and press Enter"
                  value={newEmailRecipient}
                  onChange={(e) => setNewEmailRecipient(e.target.value)}
                  onKeyDown={handleAddEmailRecipient}
                  className="border-0 flex-grow min-w-[120px] p-0 h-7 focus-visible:ring-0"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Subject:</label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Message:</label>
              <Textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={6}
              />
            </div>

            <Button 
              onClick={handleSendEmail} 
              className="w-full"
              disabled={isEmailSending}
            >
              {isEmailSending ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Email
                </>
              )}
            </Button>
          </TabsContent>

          {/* SMS Tab */}
          <TabsContent value="sms" className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">To:</label>
              <div className="flex flex-wrap gap-2 p-2 border rounded-md mb-2">
                {smsRecipients.map((recipient, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="flex items-center gap-1 py-1"
                  >
                    <span>{recipient}</span>
                    {!issue?.phoneNumber && (
                      <button
                        type="button"
                        onClick={() => handleRemoveSmsRecipient(index)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </Badge>
                ))}
                {!issue?.phoneNumber && (
                  <Input
                    type="tel"
                    placeholder="Type WhatsApp number and press Enter"
                    value={newSmsRecipient}
                    onChange={(e) => setNewSmsRecipient(e.target.value)}
                    onKeyDown={handleAddSmsRecipient}
                    className="border-0 flex-grow min-w-[120px] p-0 h-7 focus-visible:ring-0"
                  />
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">
                Upload Image: <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {!imagePreview ? (
                  <div className="border-2 border-dashed border-red-300 rounded-lg p-6 text-center hover:border-red-400 transition-colors bg-red-50">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                      required
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="h-8 w-8 text-red-400" />
                      <div>
                        <p className="text-sm font-medium text-red-600">Image is required</p>
                        <p className="text-xs text-red-500">
                          PNG, JPG, GIF up to 5MB
                        </p>
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">
                Message:
                <span className="float-right text-xs text-muted-foreground">
                  {smsMessage.length}/160 characters
                </span>
              </label>
              <Textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                rows={4}
                maxLength={160}
                placeholder="Enter your message here..."
                disabled
                readOnly
                className="bg-muted cursor-not-allowed"
              />
            </div>

            <Button 
              onClick={handleSendSms} 
              className="w-full"
              disabled={!selectedImage || isWhatsAppSending}
            >
              {isWhatsAppSending ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Sending...
                </>
              ) : (
                <>
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Send WhatsApp
                </>
              )}
            </Button>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default CommunicationPanel;
